import { useEffect } from 'react';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { token } from '@atlaskit/tokens';
import { getDocument } from '@jira/ssr__browser-globals/src/getDocument.tsx';

export const AI_PLANNER_IFRAME_MODAL_TEST_ID = 'ai-planner-iframe-modal-dialog';

const SIZING_STYLE_ELEMENT_ID = 'ai-planner-iframe-modal-sizing';

/**
 * Stretches ModalDialog's positioner to fill the viewport minus a gutter. The
 * positioner is an ancestor of the modal's own subtree, so it can only be
 * reached with a global rule. This is a workaround until a maximized ADS modal
 * is available to products.
 */
const modalSizingGlobalStyles = `
	.atlaskit-portal [data-testid="${AI_PLANNER_IFRAME_MODAL_TEST_ID}--positioner"] {
		inset: 0 !important;
		margin: auto !important;
		width: calc(100vw - ${token('space.400')});
		max-width: calc(100vw - ${token('space.400')});
		height: calc(100vh - ${token('space.400')});
		max-height: calc(100vh - ${token('space.400')});
	}
`;

/**
 * Props forwarded to the Planner iframe modal's underlying
 * `@atlaskit/modal-dialog` via `ModalTrigger`'s `modalProps`.
 *
 * A hook, not a constant, so that every trigger installs the sizing rule on
 * mount. The rule must reach the document before ModalDialog creates the
 * positioner: the modal is a lazily loaded EntryPoint, and Safari does not
 * re-resolve an already-styled positioner when the stylesheet arrives with that
 * chunk, leaving the dialog at ADS's default size. Pairing the rule with the
 * props is what stops a new trigger from silently missing it. The style element
 * is static and shared, so it is never removed.
 *
 * The iframe host is moved inside the dialog, so ADS's normal focus lock is
 * correct and no `focusLockAllowlist` is needed.
 */
export const useAiPlannerIframeModalProps = (): ModalDialogProps => {
	useEffect(() => {
		const doc = getDocument();
		if (doc == null) {
			return;
		}
		if (doc.getElementById(SIZING_STYLE_ELEMENT_ID) != null) {
			return;
		}
		const style = doc.createElement('style');
		style.id = SIZING_STYLE_ELEMENT_ID;
		style.textContent = modalSizingGlobalStyles;
		doc.head.appendChild(style);
	}, []);

	return {
		width: '100%',
		height: '100%',
		testId: AI_PLANNER_IFRAME_MODAL_TEST_ID,
	};
};
